import { getIsFrameComplete } from './completion'
import { type FrameBuilderConfigurationWithLimits } from './configuration'
import { getPriceWithDiscount, roundUp, sum } from './price'
import { getIsSizeWithinLimits } from './size'
import {
  type FrameBuilderExtraService,
  type FrameBuilderGlass,
  type FrameBuilderPassepartoutSize,
  type FrameBuilderProfile,
  type FrameBuilderState,
  type FrameBuilderTexture,
} from './state'

/**
 * Gets the size from frame state.
 */
const getStateFrameSize = (state: FrameBuilderState) => {
  const passepartoutSize = state.passepartout.size?.size ?? 0

  return [
    state.artwork.width + passepartoutSize * 2,
    state.artwork.height + passepartoutSize * 2,
  ] as const
}

/**
 * Calculates frame perimeter (in meters).
 */
const getStateFramePerimeter = (state: FrameBuilderState) => {
  const [frameWidth, frameHeight] = getStateFrameSize(state)

  return (frameWidth * 2 + frameHeight * 2) / 100
}

/**
 * Calculates farme area (in square meters).
 */
const getStateFrameArea = (state: FrameBuilderState) => {
  const [frameWidth, frameHeight] = getStateFrameSize(state)

  return (frameWidth * frameHeight) / (100 * 100)
}

/**
 * Calculates profile and texture price.
 */
const getStateProfileTextureCost = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
  profileTexturePrice: number,
  profile?: FrameBuilderProfile,
) => {
  const [frameWidth, frameHeight] = getStateFrameSize(state)
  const framePerimeter = getStateFramePerimeter(state)
  const isSizeWithinLimits = getIsSizeWithinLimits(
    frameWidth,
    frameHeight,
    profile?.minimumLargeFrameWidth,
    profile?.minimumLargeFrameHeight,
  )

  const frameMaterialCost = profileTexturePrice * framePerimeter
  const cutWasteCost =
    profileTexturePrice * configuration.servicePrices.cutWaste
  const largeFrameProductionFee = !isSizeWithinLimits
    ? configuration.servicePrices.largeFrameProductionFee
    : 0

  return [frameMaterialCost, cutWasteCost, largeFrameProductionFee].reduce(
    sum,
    0,
  )
}

/**
 * Calculates frame material, cut waste, production and backboard price with or without discount.
 */
const getStateFrameCost = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
  profile?: FrameBuilderProfile,
  texture?: FrameBuilderTexture,
  hasDiscount: boolean = false,
) => {
  const textureProfileReference = texture?.availableFor?.find(
    (linkedProfile) => linkedProfile.profile._ref === profile?._id,
  )

  if (!profile || !texture || !textureProfileReference) {
    return 0
  }

  let profileTexturePrice = profile.price + textureProfileReference.price

  if (hasDiscount) {
    profileTexturePrice = getPriceWithDiscount(
      profileTexturePrice,
      textureProfileReference.discountPercent,
    )
  }

  return getStateProfileTextureCost(
    state,
    configuration,
    profileTexturePrice,
    profile,
  )
}

/**
 * Calculates frame material, cut waste, production and backboard price with discount.
 */
export const getStateFrameCostWithDiscount = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
  profile?: FrameBuilderProfile,
  texture?: FrameBuilderTexture,
) => {
  return getStateFrameCost(state, configuration, profile, texture, true)
}

/**
 * Calculates standard size frame and glass price.
 */
export const getStateStandardSizeCost = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
) => {
  const passepartoutSize = state.passepartout.size?.size ?? 0

  if (passepartoutSize > 0) {
    return
  }

  // Find the price defined for standard size
  const standardSize = configuration.standardSizes.find(
    (standardSize) =>
      (state.artwork.width === standardSize.width &&
        state.artwork.height === standardSize.height) ||
      (state.artwork.height === standardSize.width &&
        state.artwork.width === standardSize.height),
  )

  return standardSize?.customFramePrices?.find(
    (customFramePrice) =>
      customFramePrice.profile._ref === state.profile?._id &&
      customFramePrice.texture._ref === state.texture?._id &&
      customFramePrice.glass._ref === state.glass?._id,
  )?.price
}

/**
 * Calculates passepartout price.
 */
export const getStatePassepartoutCost = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
  passepartoutSize?: FrameBuilderPassepartoutSize,
) => {
  const size = passepartoutSize?.size ?? 0

  if (size === 0) {
    return 0
  }

  // Passepartout size (in centimeters) - largest frame dimension rounded up to closest 5
  const roundedSize = roundUp(
    Math.max(state.artwork.width, state.artwork.height) + size * 2,
    5,
  )

  return Math.max(
    configuration.servicePrices.passepartout * roundedSize,
    configuration.servicePrices.passepartoutMinimum,
  )
}

/**
 * Calculates passepartout price with discount.
 */
export const getStatePassepartoutCostWithDiscount = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
  passepartoutSize?: FrameBuilderPassepartoutSize,
) =>
  getPriceWithDiscount(
    getStatePassepartoutCost(state, configuration, passepartoutSize),
    passepartoutSize?.discountPercent,
  )

/**
 * Calculates glass price.
 */
export const getStateGlassCost = (
  state: FrameBuilderState,
  glass?: FrameBuilderGlass,
) => {
  if (!glass?.price) {
    return 0
  }

  const frameArea = getStateFrameArea(state)

  return glass.price * frameArea
}

/**
 * Calculates glass price including discount.
 */
export const getStateGlassCostWithDiscount = (
  state: FrameBuilderState,
  glass?: FrameBuilderGlass,
) =>
  getPriceWithDiscount(getStateGlassCost(state, glass), glass?.discountPercent)

/**
 * Calculates total price of extra service.
 */
export const getStateExtraServicePrice = (
  state: FrameBuilderState,
  extraService: FrameBuilderExtraService,
) => {
  const frameArea = getStateFrameArea(state)

  return extraService.price + extraService.sizeBasedPrice * frameArea
}

/**
 * Calculates total price of spacer.
 */
export const getStateExtrasSpacerPrice = (state: FrameBuilderState) => {
  if (!state.extras.spacer.size) {
    return 0
  }

  const framePerimeter = getStateFramePerimeter(state)

  return (
    state.extras.spacer.size.fixedPrice +
    state.extras.spacer.size.price * framePerimeter
  )
}

/**
 * Calculates total price of options in the extras step.
 */
const getStateExtrasCost = (state: FrameBuilderState): number => {
  const extraServiceCosts =
    state.extras.extraServices?.map((extraService) =>
      getStateExtraServicePrice(state, extraService),
    ) ?? []
  const spacerCost = getStateExtrasSpacerPrice(state)

  return [...extraServiceCosts, spacerCost].reduce(sum, 0)
}

/**
 * Calculates frame price based on frame state.
 */
export const getStatePrice = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
): number => {
  const frameArea = getStateFrameArea(state)

  const productionCost = configuration.servicePrices.productionFee
  const backBoardCost = Math.max(
    configuration.servicePrices.backBoard * frameArea,
    configuration.servicePrices.backBoardMinimum,
  )
  const frameCost = getStateFrameCost(
    state,
    configuration,
    state.profile,
    state.texture,
  )
  const passepartoutCost = getStatePassepartoutCost(
    state,
    configuration,
    state.passepartout.size,
  )
  const glassCost = getStateGlassCost(state, state.glass)
  const extrasCost = state.extras.active ? getStateExtrasCost(state) : 0
  const standardSizeCost = getStateStandardSizeCost(state, configuration)

  // Override frame price with standard size price
  if (typeof standardSizeCost !== 'undefined') {
    return standardSizeCost
  }

  return [
    productionCost,
    backBoardCost,
    frameCost,
    passepartoutCost,
    glassCost,
    extrasCost,
  ].reduce(sum, 0)
}

/**
 * Calculates frame price with discounts based on frame state.
 */
export const getStatePriceWithDiscount = (
  state: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
): number => {
  const frameArea = getStateFrameArea(state)

  const productionCost = configuration.servicePrices.productionFee
  const backBoardCost = Math.max(
    configuration.servicePrices.backBoard * frameArea,
    configuration.servicePrices.backBoardMinimum,
  )
  const frameCost = getStateFrameCostWithDiscount(
    state,
    configuration,
    state.profile,
    state.texture,
  )
  const passepartoutCost = getStatePassepartoutCostWithDiscount(
    state,
    configuration,
    state.passepartout.size,
  )
  const glassCost = getStateGlassCostWithDiscount(state, state.glass)
  const extrasCost = state.extras.active ? getStateExtrasCost(state) : 0
  const standardSizeCost = getStateStandardSizeCost(state, configuration)

  // Override frame price with standard size price
  if (typeof standardSizeCost !== 'undefined') {
    return standardSizeCost
  }

  return [
    productionCost,
    backBoardCost,
    frameCost,
    passepartoutCost,
    glassCost,
    extrasCost,
  ].reduce(sum, 0)
}
